import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  TrendingUp,
  Clock,
  FileText,
  CheckCircle,
  AlertCircle,
  Download,
  Calendar,
  ChevronDown,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { Button } from "@/components/common";
import { cn } from "@/lib/utils";
import { generateUUID } from "@/utils/transform";
import { analyticsApi } from "../../services/api";

const COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#ef4444",
];

// Hook to detect dark mode for chart theming
const useChartTheme = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains("dark"));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  return {
    isDark,
    gridColor: isDark ? "rgba(255,255,255,0.1)" : "#f0f0f0",
    textColor: isDark ? "#a1a1aa" : "#71717a",
    tooltipBg: isDark ? "#27272a" : "#ffffff",
    tooltipBorder: isDark ? "#3f3f46" : "#e5e7eb",
    tooltipText: isDark ? "#fafafa" : "#18181b",
  };
};

// Custom tooltip component for dark mode support
const ChartTooltip = ({ active, payload, label, labelFormatter }) => {
  if (!active || !payload?.length) return null;

  const formattedLabel = labelFormatter ? labelFormatter(label) : label;

  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-sm shadow-md">
      {formattedLabel && (
        <p className="mb-1 font-medium text-popover-foreground">
          {formattedLabel}
        </p>
      )}
      {payload.map((entry) => (
        <p key={generateUUID()} className="text-muted-foreground">
          <span
            className="mr-2 inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

export const AnalyticsDashboard = () => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState("30d");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const chartTheme = useChartTheme();

  const getDateRange = useCallback(() => {
    // Get today's date in local timezone, then format as YYYY-MM-DD
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate start date based on selected range
    const start = new Date(today);

    switch (dateRange) {
      case "7d":
        start.setDate(start.getDate() - 6); // 7 days including today
        break;
      case "30d":
        start.setDate(start.getDate() - 29); // 30 days including today
        break;
      case "90d":
        start.setDate(start.getDate() - 89); // 90 days including today
        break;
      case "1y":
        start.setFullYear(start.getFullYear() - 1);
        start.setDate(start.getDate() + 1); // 1 year including today
        break;
      default:
        start.setDate(start.getDate() - 29);
    }

    // Format as YYYY-MM-DD using local date components
    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(start),
      endDate: formatDate(today),
    };
  }, [dateRange]);

  const dateRangeParams = useMemo(() => getDateRange(), [getDateRange]);
  const { startDate, endDate } = dateRangeParams;

  // Separate queries for better cache control
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["analytics", "dashboard", startDate, endDate],
    queryFn: ({ queryKey }) => {
      const [, , start, end] = queryKey;
      return analyticsApi
        .getDashboard({ startDate: start, endDate: end })
        .then((res) => res.data?.data);
    },
    staleTime: 0,
    gcTime: 0,
  });

  const { data: trends = [], isLoading: trendsLoading } = useQuery({
    queryKey: ["analytics", "trends", startDate, endDate],
    queryFn: ({ queryKey }) => {
      const [, , start, end] = queryKey;
      return analyticsApi
        .getTrends({ startDate: start, endDate: end })
        .then((res) => res.data?.data || []);
    },
    staleTime: 0,
    gcTime: 0,
  });

  const { data: topTemplates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["analytics", "templates", startDate, endDate],
    queryFn: ({ queryKey }) => {
      const [, , start, end] = queryKey;
      return analyticsApi
        .getTopTemplates({ startDate: start, endDate: end, limit: 5 })
        .then((res) => res.data?.data || []);
    },
    staleTime: 0,
    gcTime: 0,
  });

  const { data: busiestTimes, isLoading: timesLoading } = useQuery({
    queryKey: ["analytics", "busiest-times", startDate, endDate],
    queryFn: ({ queryKey }) => {
      const [, , start, end] = queryKey;
      return analyticsApi
        .getBusiestTimes({ startDate: start, endDate: end })
        .then((res) => res.data?.data);
    },
    staleTime: 0,
    gcTime: 0,
  });

  const { data: funnel, isLoading: funnelLoading } = useQuery({
    queryKey: ["analytics", "funnel", startDate, endDate],
    queryFn: ({ queryKey }) => {
      const [, , start, end] = queryKey;
      return analyticsApi
        .getFunnel({ startDate: start, endDate: end })
        .then((res) => res.data?.data);
    },
    staleTime: 0,
    gcTime: 0,
  });

  const { data: envelopeAnalytics, isLoading: envelopeAnalyticsLoading } =
    useQuery({
      queryKey: ["analytics", "envelopes", startDate, endDate],
      queryFn: ({ queryKey }) => {
        const [, , start, end] = queryKey;
        return analyticsApi
          .getEnvelopeAnalytics({ startDate: start, endDate: end })
          .then((res) => res.data?.data);
      },
      staleTime: 0,
      gcTime: 0,
    });

  const { data: bottlenecks } = useQuery({
    queryKey: ["analytics", "bottlenecks", startDate, endDate],
    queryFn: ({ queryKey }) => {
      const [, , start, end] = queryKey;
      return analyticsApi
        .getBottlenecks({ startDate: start, endDate: end, limit: 5 })
        .then((res) => res.data?.data);
    },
    staleTime: 0,
    gcTime: 0,
    retry: false, // Don't retry if feature not available
  });

  const loading =
    statsLoading ||
    trendsLoading ||
    templatesLoading ||
    timesLoading ||
    funnelLoading ||
    envelopeAnalyticsLoading;

  const handleExport = async () => {
    try {
      const response = await analyticsApi.export({
        startDate,
        endDate,
        format: "csv",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `analytics-${dateRange}.csv`;
      a.click();
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const renderBottleneckClass = (index) => {
    if (index === 0) {
      return "bg-red-500";
    }
    return index === 1 ? "bg-orange-500" : "bg-yellow-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("nav.analytics")}
          </h1>
          <p className="text-muted-foreground">
            {t("analytics.trackPerformance")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center gap-2 px-4 py-2 bg-background border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Calendar className="w-4 h-4" aria-hidden="true" />
              <span>
                {dateRange === "7d" && t("analytics.last7Days", "Last 7 days")}
                {dateRange === "30d" &&
                  t("analytics.last30Days", "Last 30 days")}
                {dateRange === "90d" &&
                  t("analytics.last90Days", "Last 90 days")}
                {dateRange === "1y" && t("analytics.lastYear", "Last year")}
              </span>
              <ChevronDown className="w-4 h-4" aria-hidden="true" />
            </button>
            {showDatePicker && (
              <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-lg z-10">
                {["7d", "30d", "90d", "1y"].map((range) => (
                  <button
                    key={range}
                    onClick={() => {
                      setDateRange(range);
                      setShowDatePicker(false);
                    }}
                    className={cn(
                      "w-full px-4 py-2 text-left hover:bg-muted transition-colors first:rounded-t-lg last:rounded-b-lg",
                      dateRange === range && "bg-primary/10 text-primary",
                    )}
                  >
                    {range === "7d" && t("analytics.last7Days", "Last 7 days")}
                    {range === "30d" &&
                      t("analytics.last30Days", "Last 30 days")}
                    {range === "90d" &&
                      t("analytics.last90Days", "Last 90 days")}
                    {range === "1y" && t("analytics.lastYear", "Last year")}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("common.export", "Export")}
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t("analytics.envelopesSent", "Envelopes Sent")}
          value={stats?.overview?.envelopesSent || 0}
          icon={FileText}
          color="primary"
        />
        <StatCard
          title={t("analytics.completed", "Completed")}
          value={stats?.overview?.envelopesCompleted || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title={t("analytics.completionRate", "Completion Rate")}
          value={`${stats?.overview?.completionRate || 0}%`}
          icon={TrendingUp}
          color="blue"
        />
        <StatCard
          title={t("analytics.avgTimeToSign", "Avg. Time to Sign")}
          value={stats?.overview?.avgTimeToSign || "—"}
          icon={Clock}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <h3 className="text-lg font-semibold mb-4">
            {t("envelopes.activity")}
          </h3>
          {Array.isArray(trends) && trends.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trends}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartTheme.gridColor}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => {
                    try {
                      return new Date(date).toLocaleDateString("en-AU", {
                        month: "short",
                        day: "numeric",
                      });
                    } catch {
                      return "";
                    }
                  }}
                  fontSize={12}
                  tick={{ fill: chartTheme.textColor }}
                />
                <YAxis fontSize={12} tick={{ fill: chartTheme.textColor }} />
                <Tooltip
                  content={
                    <ChartTooltip
                      labelFormatter={(date) => {
                        try {
                          return new Date(date).toLocaleDateString();
                        } catch {
                          return "";
                        }
                      }}
                    />
                  }
                />
                <Legend wrapperStyle={{ color: chartTheme.textColor }} />
                <Area
                  type="monotone"
                  dataKey="sent"
                  stackId="1"
                  stroke="#6366f1"
                  fill="#6366f1"
                  fillOpacity={0.6}
                  name={t("analytics.sent", "Sent")}
                />
                <Area
                  type="monotone"
                  dataKey="completed"
                  stackId="2"
                  stroke="#10b981"
                  fill="#10b981"
                  fillOpacity={0.6}
                  name={t("analytics.completed", "Completed")}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {t("analytics.noTrendData")}
            </p>
          )}
        </div>

        {/* Device Breakdown */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <h3 className="text-lg font-semibold mb-4">
            {t("analytics.deviceBreakdown")}
          </h3>
          {(() => {
            const deviceData = [
              {
                name: t("analytics.desktop", "Desktop"),
                value: stats?.devices?.desktop?.count ?? 0,
              },
              {
                name: t("analytics.mobile", "Mobile"),
                value: stats?.devices?.mobile?.count ?? 0,
              },
              {
                name: t("analytics.tablet", "Tablet"),
                value: stats?.devices?.tablet?.count ?? 0,
              },
            ].filter((item) => item.value > 0);

            if (deviceData.length === 0) {
              return (
                <p className="text-muted-foreground text-center py-8">
                  {t("analytics.noDeviceData")}
                </p>
              );
            }

            return (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={deviceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {deviceData.map((entry, index) => (
                      <Cell
                        key={generateUUID()}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ color: chartTheme.textColor }} />
                </PieChart>
              </ResponsiveContainer>
            );
          })()}
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Funnel */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <h3 className="text-lg font-semibold mb-4">
            {t("analytics.conversionFunnel")}
          </h3>
          {funnel?.stages &&
          Array.isArray(funnel.stages) &&
          funnel.stages.length > 0 ? (
            <div className="space-y-4">
              {funnel.stages.map((stage, index) => (
                <div key={stage.name || index}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{stage.name}</span>
                    <span className="text-muted-foreground">
                      {stage.count} ({stage.percentage}%)
                    </span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${stage.percentage}%`,
                        backgroundColor: COLORS[index % COLORS.length],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {t("analytics.noFunnelData")}
            </p>
          )}
        </div>

        {/* Top Templates */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <h3 className="text-lg font-semibold mb-4">
            {t("admin.topTemplates")}
          </h3>
          {Array.isArray(topTemplates) && topTemplates.length > 0 ? (
            <div className="space-y-3">
              {topTemplates.map((template, index) => (
                <div key={template.id} className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{template.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {template.usage_count} {t("analytics.uses", "uses")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {t("templates.noTemplateData")}
            </p>
          )}
        </div>

        {/* Busiest Times */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <h3 className="text-lg font-semibold mb-4">
            {t("analytics.busiestDays")}
          </h3>
          {busiestTimes?.byDayOfWeek &&
          Array.isArray(busiestTimes.byDayOfWeek) &&
          busiestTimes.byDayOfWeek.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={busiestTimes.byDayOfWeek}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartTheme.gridColor}
                />
                <XAxis
                  dataKey="day"
                  fontSize={11}
                  tickFormatter={(day) => day?.slice(0, 3) || ""}
                  tick={{ fill: chartTheme.textColor }}
                />
                <YAxis fontSize={12} tick={{ fill: chartTheme.textColor }} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {t("common.noDataAvailable")}
            </p>
          )}
        </div>
      </div>

      {/* Drop-off Analysis */}
      {funnel?.dropOff && (
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <h3 className="text-lg font-semibold mb-4">
            {t("analytics.dropOffAnalysis")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <DropOffCard
              title={t("analytics.sentToViewed", "Sent → Viewed")}
              percentage={funnel.dropOff.sentToViewed}
              description={t(
                "analytics.notOpened",
                "Recipients who haven't opened",
              )}
            />
            <DropOffCard
              title={t("analytics.viewedToSigned", "Viewed → Signed")}
              percentage={funnel.dropOff.viewedToSigned}
              description={t("analytics.viewedNotSigned")}
            />
            <DropOffCard
              title={t("analytics.signedToCompleted", "Signed → Completed")}
              percentage={funnel.dropOff.signedToCompleted}
              description={t("analytics.waitingForOthers")}
            />
          </div>
        </div>
      )}

      {/* Envelope Performance Section */}
      {envelopeAnalytics && (
        <>
          {/* Timing Metrics */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold mb-4">
              {t("analytics.completionTiming", "Completion Timing")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <TimingCard
                label={t("analytics.average", "Average")}
                value={envelopeAnalytics.timing?.avgFormatted}
                sublabel={
                  envelopeAnalytics.timing?.avgHoursToComplete
                    ? `${envelopeAnalytics.timing.avgHoursToComplete}h`
                    : null
                }
              />
              <TimingCard
                label={t("analytics.median", "Median")}
                value={envelopeAnalytics.timing?.medianFormatted}
                sublabel={
                  envelopeAnalytics.timing?.medianHoursToComplete
                    ? `${envelopeAnalytics.timing.medianHoursToComplete}h`
                    : null
                }
              />
              <TimingCard
                label={t("analytics.fastest", "Fastest")}
                value={envelopeAnalytics.timing?.minFormatted}
                color="green"
              />
              <TimingCard
                label={t("analytics.slowest", "Slowest")}
                value={envelopeAnalytics.timing?.maxFormatted}
                color="orange"
              />
            </div>
          </div>

          {/* Completion Time Distribution & Weekly Trend */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Completion Time Distribution */}
            <div className="bg-card rounded-xl shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">
                {t(
                  "analytics.timeDistribution",
                  "Time to Complete Distribution",
                )}
              </h3>
              {envelopeAnalytics.distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={envelopeAnalytics.distribution}
                    layout="vertical"
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={chartTheme.gridColor}
                    />
                    <XAxis
                      type="number"
                      fontSize={12}
                      tick={{ fill: chartTheme.textColor }}
                    />
                    <YAxis
                      type="category"
                      dataKey="bucket"
                      fontSize={11}
                      width={90}
                      tick={{ fill: chartTheme.textColor }}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  {t("common.noDataAvailable")}
                </p>
              )}
            </div>

            {/* Weekly Trend */}
            <div className="bg-card rounded-xl shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">
                {t("analytics.weeklyTrend", "Weekly Performance")}
              </h3>
              {envelopeAnalytics.weeklyTrend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={envelopeAnalytics.weeklyTrend}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={chartTheme.gridColor}
                    />
                    <XAxis
                      dataKey="weekStart"
                      tickFormatter={(date) => {
                        try {
                          return new Date(date).toLocaleDateString("en-AU", {
                            month: "short",
                            day: "numeric",
                          });
                        } catch {
                          return "";
                        }
                      }}
                      fontSize={11}
                      tick={{ fill: chartTheme.textColor }}
                    />
                    <YAxis
                      fontSize={12}
                      tick={{ fill: chartTheme.textColor }}
                    />
                    <Tooltip
                      content={
                        <ChartTooltip
                          labelFormatter={(date) => {
                            try {
                              return `${t("analytics.weekOf", "Week of")} ${new Date(date).toLocaleDateString()}`;
                            } catch {
                              return "";
                            }
                          }}
                        />
                      }
                    />
                    <Legend wrapperStyle={{ color: chartTheme.textColor }} />
                    <Bar
                      dataKey="sent"
                      name={t("analytics.sent", "Sent")}
                      fill="#6366f1"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="completed"
                      name={t("analytics.completed", "Completed")}
                      fill="#10b981"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  {t("common.noDataAvailable")}
                </p>
              )}
            </div>
          </div>

          {/* Recent Envelopes */}
          {envelopeAnalytics.recentEnvelopes?.length > 0 && (
            <div className="bg-card rounded-xl shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">
                {t("analytics.recentEnvelopes", "Recent Completed Envelopes")}
              </h3>
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm"
                  aria-label={t(
                    "analytics.recentEnvelopes",
                    "Recent Completed Envelopes",
                  )}
                >
                  <thead>
                    <tr className="border-b border-border">
                      <th
                        scope="col"
                        className="text-left py-3 px-2 font-medium text-muted-foreground"
                      >
                        {t("common.title")}
                      </th>
                      <th
                        scope="col"
                        className="text-left py-3 px-2 font-medium text-muted-foreground"
                      >
                        {t("common.status")}
                      </th>
                      <th
                        scope="col"
                        className="text-right py-3 px-2 font-medium text-muted-foreground"
                      >
                        {t("analytics.recipients", "Recipients")}
                      </th>
                      <th
                        scope="col"
                        className="text-right py-3 px-2 font-medium text-muted-foreground"
                      >
                        {t("analytics.timeToComplete", "Time to Complete")}
                      </th>
                      <th
                        scope="col"
                        className="text-right py-3 px-2 font-medium text-muted-foreground"
                      >
                        {t("analytics.completedDate", "Completed")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {envelopeAnalytics.recentEnvelopes.map((env) => (
                      <tr
                        key={env.id}
                        className="border-b border-border/50 hover:bg-muted/50 transition-colors"
                      >
                        <td className="py-3 px-2 font-medium truncate max-w-xs">
                          {env.title || t("common.untitled", "Untitled")}
                        </td>
                        <td className="py-3 px-2">
                          <StatusBadge status={env.status} />
                        </td>
                        <td className="py-3 px-2 text-right">
                          {env.signedCount}/{env.recipientCount}
                        </td>
                        <td className="py-3 px-2 text-right font-mono">
                          {env.hoursFormatted || "—"}
                        </td>
                        <td className="py-3 px-2 text-right text-muted-foreground">
                          {env.completedAt
                            ? new Date(env.completedAt).toLocaleDateString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Recipient Bottlenecks (Advanced Analytics) */}
      {bottlenecks?.recipients?.length > 0 && (
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {t("analytics.bottlenecks", "Recipient Bottlenecks")}
            </h3>
            <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded">
              {t("analytics.advancedFeature", "Advanced")}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {t(
              "analytics.bottlenecksDescription",
              "Recipients with the slowest average signing times (minimum 2 envelopes)",
            )}
          </p>
          <div className="space-y-3">
            {bottlenecks.recipients.map((recipient, index) => (
              <div
                key={recipient.email}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium",
                      renderBottleneckClass(index),
                    )}
                  >
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium">
                      {recipient.name || recipient.email}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {recipient.email}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono font-medium">
                    {recipient.avgFormatted}
                    <span className="text-muted-foreground text-xs ml-1">
                      {t("analytics.avg", "avg")}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {recipient.signedCount}/{recipient.totalEnvelopes}{" "}
                    {t("analytics.signed", "signed")} ({recipient.signRate}%)
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    primary: "bg-primary/10 text-primary",
    green:
      "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400",
    blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400",
    purple:
      "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400",
  };

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border p-6">
      <div className="flex items-center gap-4">
        <div className={cn("p-3 rounded-lg", colorClasses[color])}>
          <Icon className="w-6 h-6" aria-hidden="true" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  );
};

const DropOffCard = ({ title, percentage, description }) => {
  const isHigh = parseFloat(percentage) > 30;

  return (
    <div
      className={cn(
        "p-4 rounded-lg",
        isHigh ? "bg-red-50 dark:bg-red-900/20" : "bg-muted",
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        {isHigh ? (
          <AlertCircle className="w-5 h-5 text-red-500" aria-hidden="true" />
        ) : (
          <CheckCircle className="w-5 h-5 text-green-500" aria-hidden="true" />
        )}
        <span className="font-medium">{title}</span>
      </div>
      <p
        className={cn(
          "text-2xl font-bold",
          isHigh ? "text-red-600 dark:text-red-400" : "text-foreground",
        )}
      >
        {percentage}%
      </p>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
};

const TimingCard = ({ label, value, sublabel, color = "default" }) => {
  const colorClasses = {
    default: "text-foreground",
    green: "text-green-600 dark:text-green-400",
    orange: "text-orange-600 dark:text-orange-400",
    red: "text-red-600 dark:text-red-400",
  };

  return (
    <div className="bg-muted/50 rounded-lg p-4 text-center">
      <p className="text-sm text-muted-foreground mb-1">{label}</p>
      <p className={cn("text-2xl font-bold", colorClasses[color])}>
        {value || "—"}
      </p>
      {sublabel && (
        <p className="text-xs text-muted-foreground mt-1">{sublabel}</p>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    completed: {
      className:
        "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300",
      label: "Completed",
    },
    declined: {
      className: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300",
      label: "Declined",
    },
    voided: {
      className:
        "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300",
      label: "Voided",
    },
    expired: {
      className:
        "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
      label: "Expired",
    },
  };

  const config = statusConfig[status] || statusConfig.completed;

  return (
    <span
      className={cn("px-2 py-1 rounded text-xs font-medium", config.className)}
    >
      {config.label}
    </span>
  );
};

export default AnalyticsDashboard;
